/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Error} from '../commons';

import DigioFlow from '../DigioFlow';
import {ProgressBar} from 'components/ProgressBar';
import kycScreenMapping from 'types/KycScreenMapping';

// Stage-based heading and subheading mapping
const STAGE_PROGRESS_MAPPING = {
  [kycScreenMapping.DIGILOCKER]: {
    heading: 'Fetching Your Documents',
    subheading: 'Please wait while we securely access your documents from DigiLocker',
  },
  [kycScreenMapping.SELFIE]: {
    heading: 'Processing Selfie Verification',
    subheading: 'Please wait while we verify your selfie for identity confirmation',
  },
  [kycScreenMapping.PAN_ANALYSIS]: {
    heading: 'Analyzing PAN Details',
    subheading: 'Please wait while we verify and process your PAN information',
  },
  [kycScreenMapping.UPLOAD_PAN]: {
    heading: 'Processing PAN Upload',
    subheading: 'Please wait while we validate your uploaded PAN document',
  },
  [kycScreenMapping.E_SIGN]: {
    heading: 'Processing E-Signature',
    subheading: 'Please wait while we complete your electronic signature verification',
  },
  [kycScreenMapping.DDPI]: {
    heading: 'Processing DDPI',
    subheading: 'Please wait while we set up your DDPI (Demat Debit and Pledge Instruction)',
  },
  [kycScreenMapping.SPECIMEN_SIGN]: {
    heading: 'Processing Specimen Signature',
    subheading: 'Please wait while we verify your specimen signature',
  },
  // Default fallback
  DEFAULT: {
    heading: 'Processing to next step',
    subheading: 'Please wait while we fetch your kyc details',
  },
};

const propTypes = {
  kycData: PropTypes.object.isRequired,
  updateStatus: PropTypes.func.isRequired,
  handleErrorState: PropTypes.func,
};

const InitiateApiCalls = ({isError, digioDetails, kycData, updateStatus, handleErrorState}) => {
  // Get custom heading and subheading based on current stage
  const currentStage = kycData?.stage?.toUpperCase();
  const progressContent = STAGE_PROGRESS_MAPPING[currentStage] || STAGE_PROGRESS_MAPPING.DEFAULT;

  return (
    <>
      {digioDetails && !!digioDetails.kid ? (
        <DigioFlow
          requestId={digioDetails.kid}
          identifier={digioDetails.customerIdentifier}
          tokenId={digioDetails.tokenId}
          updateStatus={updateStatus}
          kycData={kycData}
          handleErrorState={handleErrorState}
        />
      ) : isError ? (
        <ErrorContainer>
          <Error message="Please retry, if it still doesn't work, try again after sometime" />
        </ErrorContainer>
      ) : (
        <LoaderContainer>
          <ProgressBar
            subheading={progressContent.subheading}
            heading={progressContent.heading}
            waitTimeInSec={180}
            stopOnEndTime={true}
            stopOnEndMsg={'Something went wrong.Please try again'}
            showShadow={false}
          />
        </LoaderContainer>
      )}
    </>
  );
};

const LoaderContainer = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => props.theme.colors.background.primary};
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  padding: ${(props) => props.theme.spacing.md};

  @media (max-width: ${(props) => props.theme.breakpoints.md}) {
    padding: ${(props) => props.theme.spacing.sm};
  }
`;

const ErrorContainer = styled.div`
  height: 100%;
  width: 100%;
  scroll-behavior: smooth;
  overflow-y: auto;
  padding: ${(props) => props.theme.spacing.md};
  text-align: center;
  background-color: ${(props) => props.theme.colors.background.primary};
  font-family: ${(props) => props.theme.typography.fontFamily.primary};
  color: ${(props) => props.theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

InitiateApiCalls.propTypes = propTypes;

export default InitiateApiCalls;
